import React, { useEffect, useState, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import {
  Globe, Link2, CheckCircle2, Clock, AlertCircle, Crown, Sparkles, Copy, ShieldCheck,
  Plus, X, RefreshCw, Loader2, Trash2,
} from 'lucide-react';
import { useSubscription } from '../../context/SubscriptionContext';
import { useTenantBranding } from '../../context/TenantBrandingContext';
import { subscriptionAPI, domainsAPI } from '../../services/api';

/**
 * DomainManagement — Phase 1
 *
 * Three UI states depending on the tenant's plan/add-ons:
 *   1) has_custom_domain (via plan or active add-on) → full domain manager
 *      (Phase 1 stub; full CRUD + verification flow ships in Phase 9)
 *   2) has_website but NOT has_custom_domain, and add-on is purchasable →
 *      show two paths: "Upgrade plan" OR "Buy add-on" with price from Super Admin
 *   3) has_website but add-on not configured → upgrade-only CTA
 *
 * The subdomain URL (`<slug>.sites.listechs.com`) is always shown as read-only.
 */

const LISTECHS_SITES_ROOT = 'sites.listechs.com';

// ─── Custom domains manager (Phase 9) ─────────────────────────────
//
// Shown when the tenant has the `has_custom_domain` feature (via plan or
// active add-on). Lists registered domains with verification status, lets
// the admin add a new one, kicks off verification on demand, and removes
// domains.
//
// Verification UX: after `add()`, we show instructions inline (CNAME + TXT
// records); the admin adds them at their DNS provider, then clicks "تحقق".
// The backend runs a DNS lookup and flips the status.

function DomainStatusBadge({ status }) {
  const map = {
    pending:     { label: 'بانتظار التحقق', cls: 'bg-amber-50 text-amber-700 border-amber-200' },
    verifying:   { label: 'جاري التحقق',    cls: 'bg-blue-50 text-blue-700 border-blue-200' },
    verified:    { label: 'تم التحقق',      cls: 'bg-emerald-50 text-emerald-700 border-emerald-200' },
    active:      { label: 'فعّال',          cls: 'bg-emerald-50 text-emerald-700 border-emerald-200' },
    ssl_issuing: { label: 'إصدار SSL',      cls: 'bg-blue-50 text-blue-600 border-blue-200' },
    failed:      { label: 'فشل التحقق',     cls: 'bg-blue-50 text-blue-700 border-blue-200' },
    revoked:     { label: 'موقوف',          cls: 'bg-gray-100 text-gray-600 border-gray-200' },
  };
  const m = map[status] || map.pending;
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-medium border ${m.cls}`}>
      {m.label}
    </span>
  );
}

function SslBadge({ status }) {
  const map = {
    none:    { label: 'بدون SSL',       cls: 'bg-gray-100 text-gray-500' },
    pending: { label: 'SSL قيد الإصدار', cls: 'bg-blue-50 text-blue-600' },
    issued:  { label: 'SSL فعّال',       cls: 'bg-emerald-50 text-emerald-700' },
    failed:  { label: 'فشل SSL',         cls: 'bg-blue-50 text-blue-700' },
    expired: { label: 'SSL منتهٍ',       cls: 'bg-amber-50 text-amber-700' },
  };
  const m = map[status] || map.none;
  return <span className={`inline-block px-1.5 py-0 rounded text-[10px] font-medium ${m.cls}`}>{m.label}</span>;
}

function CustomDomainsManager() {
  const [loading, setLoading] = useState(true);
  const [items, setItems] = useState([]);
  const [cnameTarget, setCnameTarget] = useState('edge.sites.listechs.com');
  const [adding, setAdding] = useState(false);
  const [newDomain, setNewDomain] = useState('');
  const [busyId, setBusyId] = useState(null);
  const [pendingInstructions, setPendingInstructions] = useState(null); // { domainId, cnameRecord, txtRecord }
  const [errorMsg, setErrorMsg] = useState(null);

  const fetchDomains = useCallback(async () => {
    setLoading(true);
    setErrorMsg(null);
    try {
      const resp = await domainsAPI.list();
      const data = resp?.data?.data || resp?.data || {};
      setItems(Array.isArray(data.customDomains) ? data.customDomains : []);
      if (data.cnameTarget) setCnameTarget(data.cnameTarget);
    } catch (err) {
      setErrorMsg(err?.response?.data?.message || 'تعذّر تحميل الدومينات');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchDomains(); }, [fetchDomains]);

  const onAdd = useCallback(async (e) => {
    e?.preventDefault?.();
    const clean = newDomain.trim();
    if (!clean) return;
    setAdding(true);
    try {
      const resp = await domainsAPI.add(clean);
      const data = resp?.data?.data || resp?.data || {};
      setNewDomain('');
      toast.success('تم تسجيل الدومين — اتبع تعليمات التحقق');
      // Refresh list and auto-open instructions for the one we just added
      await fetchDomains();
      if (data?.domain?._id && data?.instructions) {
        setPendingInstructions({
          domainId: data.domain._id,
          domain: data.domain.domain,
          cnameRecord: data.instructions.cnameRecord,
          txtRecord: data.instructions.txtRecord,
          notes: data.instructions.notes,
        });
      }
    } catch (err) {
      toast.error(err?.response?.data?.message || 'فشل تسجيل الدومين');
    } finally {
      setAdding(false);
    }
  }, [newDomain, fetchDomains]);

  const onVerify = useCallback(async (domainId) => {
    setBusyId(domainId);
    try {
      const resp = await domainsAPI.verify(domainId);
      const data = resp?.data?.data?.domain || resp?.data?.data || resp?.data;
      if (data?.status === 'active' || data?.status === 'verified') {
        toast.success('تم التحقق بنجاح');
      } else if (data?.status === 'failed') {
        toast.error(data.errorMessage || 'فشل التحقق — راجع سجلات الـ DNS');
      } else {
        toast('جاري التحقق، قد تستغرق السجلات وقتاً للانتشار');
      }
      await fetchDomains();
    } catch (err) {
      toast.error(err?.response?.data?.message || 'فشل التحقق');
    } finally {
      setBusyId(null);
    }
  }, [fetchDomains]);

  const onRemove = useCallback(async (domainId, domain) => {
    if (!window.confirm(`حذف الدومين "${domain}"؟ سيتوقف عمله فوراً.`)) return;
    setBusyId(domainId);
    try {
      await domainsAPI.remove(domainId);
      toast.success('تم حذف الدومين');
      await fetchDomains();
    } catch (err) {
      toast.error(err?.response?.data?.message || 'فشل الحذف');
    } finally {
      setBusyId(null);
    }
  }, [fetchDomains]);

  const copy = (text) => {
    navigator.clipboard?.writeText(text);
    toast.success('تم النسخ');
  };

  return (
    <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
      <div className="p-6 border-b border-gray-100">
        <div className="flex items-center gap-2 mb-1">
          <Link2 className="w-4 h-4 text-blue-600" />
          <span className="text-sm font-semibold text-blue-700">الدومين المخصص — مفعّل</span>
        </div>
        <p className="text-sm text-gray-600">
          اربط دومينك الخاص (مثل <code className="text-gray-800">mylab.com</code>) بموقع مختبرك. نتحقق تلقائياً من سجلات الـ DNS كل 5 دقائق.
        </p>
      </div>

      {/* Add form */}
      <form onSubmit={onAdd} className="p-6 border-b border-gray-100 bg-gradient-to-br from-gray-50 to-white">
        <label className="block text-sm font-medium text-gray-700 mb-1.5">إضافة دومين جديد</label>
        <div className="flex gap-2">
          <input
            type="text"
            value={newDomain}
            onChange={(e) => setNewDomain(e.target.value)}
            placeholder="example: mylab.com"
            dir="ltr"
            className="flex-1 px-4 py-2.5 border border-gray-200 rounded-xl text-sm font-mono focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500"
          />
          <button
            type="submit"
            disabled={!newDomain.trim() || adding}
            className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-medium bg-gradient-to-r from-blue-600 to-blue-700 text-white disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {adding ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
            إضافة
          </button>
        </div>
        <p className="text-xs text-gray-500 mt-1.5">أدخل الدومين بدون http/https ولا شرطة / في النهاية.</p>
      </form>

      {/* Instructions modal-ish panel (inline) */}
      <AnimatePresence>
        {pendingInstructions && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="overflow-hidden"
          >
            <div className="m-6 p-5 rounded-2xl bg-amber-50 border border-amber-200">
              <div className="flex items-start justify-between gap-3 mb-3">
                <div>
                  <h4 className="text-sm font-bold text-amber-900">أضف سجلّي DNS التاليين في لوحة مزوّد الدومين</h4>
                  <p className="text-xs text-amber-700 mt-0.5">
                    الدومين: <span className="font-mono">{pendingInstructions.domain}</span> — بعد الإضافة اضغط "تحقق" في القائمة أدناه.
                  </p>
                </div>
                <button
                  onClick={() => setPendingInstructions(null)}
                  className="p-1.5 rounded text-amber-700 hover:bg-amber-100"
                  aria-label="إغلاق"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>

              {/* CNAME record card */}
              <div className="rounded-xl bg-white border border-amber-100 p-3 mb-2">
                <div className="text-[11px] font-semibold text-amber-700 mb-1">السجل الأول — CNAME</div>
                <div className="grid sm:grid-cols-4 gap-3 text-xs">
                  <Field label="النوع" value="CNAME" onCopy={copy} />
                  <Field label="Host" value={pendingInstructions.cnameRecord.host} onCopy={copy} mono />
                  <Field label="Value" value={pendingInstructions.cnameRecord.value} onCopy={copy} mono />
                  <Field label="TTL" value="300" onCopy={copy} />
                </div>
              </div>

              {/* TXT record card */}
              <div className="rounded-xl bg-white border border-amber-100 p-3">
                <div className="text-[11px] font-semibold text-amber-700 mb-1">السجل الثاني — TXT (للتحقق من الملكية)</div>
                <div className="grid sm:grid-cols-4 gap-3 text-xs">
                  <Field label="النوع" value="TXT" onCopy={copy} />
                  <Field label="Host" value={pendingInstructions.txtRecord.host} onCopy={copy} mono />
                  <Field label="Value" value={pendingInstructions.txtRecord.value} onCopy={copy} mono />
                  <Field label="TTL" value="300" onCopy={copy} />
                </div>
              </div>

              {pendingInstructions.notes?.length > 0 && (
                <ul className="mt-3 space-y-1 text-xs text-amber-800">
                  {pendingInstructions.notes.map((n, i) => (
                    <li key={i} className="flex items-start gap-2">
                      <span className="w-1 h-1 rounded-full bg-amber-500 mt-1.5 shrink-0" />
                      <span>{n}</span>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* List */}
      <div className="p-6">
        {loading ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="w-6 h-6 text-blue-600 animate-spin" />
          </div>
        ) : errorMsg ? (
          <div className="p-4 rounded-xl border border-blue-100 bg-blue-50 text-sm text-blue-700 flex items-start gap-2">
            <AlertCircle className="w-4 h-4 mt-0.5" />
            {errorMsg}
            <button onClick={fetchDomains} className="mr-auto underline">إعادة المحاولة</button>
          </div>
        ) : items.length === 0 ? (
          <div className="text-center py-8 text-sm text-gray-400">
            لا توجد دومينات مخصصة بعد — أضف الأول من الحقل أعلاه
          </div>
        ) : (
          <div className="space-y-2">
            {items.map((d) => {
              const canVerify = ['pending', 'failed', 'verifying'].includes(d.status);
              const isBusy = busyId === d._id;
              return (
                <div key={d._id} className="flex items-center gap-3 p-3 rounded-xl border border-gray-100 hover:border-gray-200 transition-colors">
                  <div className="w-8 h-8 rounded-lg bg-blue-50 text-blue-600 flex items-center justify-center shrink-0">
                    <Globe className="w-4 h-4" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-sm font-semibold text-gray-800 font-mono truncate" dir="ltr">{d.domain}</span>
                      <DomainStatusBadge status={d.status} />
                      {d.sslStatus && d.sslStatus !== 'none' && <SslBadge status={d.sslStatus} />}
                    </div>
                    {d.status === 'failed' && d.errorMessage && (
                      <div className="text-xs text-blue-600 mt-1 truncate" title={d.errorMessage}>
                        {d.errorMessage}
                      </div>
                    )}
                    {d.lastCheckedAt && (
                      <div className="text-[11px] text-gray-400 mt-0.5">
                        آخر فحص: {new Date(d.lastCheckedAt).toLocaleString('ar-SA-u-ca-gregory')}
                      </div>
                    )}
                  </div>
                  <div className="flex items-center gap-1 shrink-0">
                    {canVerify && (
                      <button
                        onClick={() => onVerify(d._id)}
                        disabled={isBusy}
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium text-blue-700 bg-blue-50 hover:bg-blue-100 disabled:opacity-50"
                      >
                        {isBusy ? <Loader2 className="w-3 h-3 animate-spin" /> : <RefreshCw className="w-3 h-3" />}
                        تحقق
                      </button>
                    )}
                    <button
                      onClick={() => setPendingInstructions({
                        domainId: d._id,
                        domain: d.domain,
                        cnameRecord: { host: d.domain, type: 'CNAME', value: d.targetCname || 'edge.sites.listechs.com', ttl: 300 },
                        txtRecord: { host: `_listechs-verify.${d.domain}`, type: 'TXT', value: `listechs-verify=${d.verificationToken}`, ttl: 300 },
                        notes: [
                          'أضف كلا السجلين (CNAME + TXT) في لوحة DNS الخاصة بمزوّد الدومين.',
                          'قد يستغرق انتشار السجلات حتى 48 ساعة.',
                        ],
                      })}
                      className="p-1.5 rounded-lg text-gray-500 hover:text-gray-800 hover:bg-gray-50"
                      title="عرض تعليمات DNS"
                    >
                      <Copy className="w-3.5 h-3.5" />
                    </button>
                    <button
                      onClick={() => onRemove(d._id, d.domain)}
                      disabled={isBusy}
                      className="p-1.5 rounded-lg text-gray-500 hover:text-blue-600 hover:bg-blue-50 disabled:opacity-50"
                      title="حذف"
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Reference footer */}
      <div className="px-6 py-3 bg-gray-50 border-t border-gray-100 text-[11px] text-gray-500">
        الـ CNAME target المعتمد: <code className="font-mono text-gray-700" dir="ltr">{cnameTarget}</code>
      </div>
    </div>
  );
}

function Field({ label, value, onCopy, mono = false }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-wider text-gray-400 mb-0.5">{label}</div>
      <div className="flex items-center gap-1">
        <code className={`flex-1 px-2 py-1 rounded bg-gray-50 border border-gray-100 text-gray-800 truncate ${mono ? 'font-mono' : ''}`} dir="ltr">
          {value}
        </code>
        <button
          type="button"
          onClick={() => onCopy(String(value))}
          className="p-1 rounded text-gray-400 hover:text-blue-600 hover:bg-blue-50"
          title="نسخ"
          aria-label="نسخ"
        >
          <Copy className="w-3 h-3" />
        </button>
      </div>
    </div>
  );
}

const DomainManagement = () => {
  const navigate = useNavigate();
  const { canAccessFeature, refreshSubscription } = useSubscription();
  const { tenant } = useTenantBranding();
  const slug = tenant?.slug || '';
  const subdomainUrl = slug ? `https://${slug}.${LISTECHS_SITES_ROOT}` : '';

  const [addonsData, setAddonsData] = useState(null);
  const [loadingAddons, setLoadingAddons] = useState(true);
  const [purchasing, setPurchasing] = useState(false);
  const [billingCycle, setBillingCycle] = useState('monthly');

  const canUseCustomDomain = canAccessFeature('has_custom_domain');

  const loadAddons = useCallback(async () => {
    try {
      setLoadingAddons(true);
      const { data } = await subscriptionAPI.getAddons();
      setAddonsData(data?.data || data);
    } catch (err) {
      console.warn('Failed to load addons', err);
    } finally {
      setLoadingAddons(false);
    }
  }, []);

  useEffect(() => {
    loadAddons();
  }, [loadAddons]);

  const customDomainAddon = addonsData?.available?.find((a) => a.key === 'custom_domain');
  const ownedAddon = addonsData?.owned?.find((a) => a.key === 'custom_domain');
  const canPurchaseAddon = !!customDomainAddon?.purchasable;

  const onPurchase = async () => {
    if (purchasing) return;
    try {
      setPurchasing(true);
      await subscriptionAPI.purchaseCustomDomain({ billingCycle });
      toast.success('تم تسجيل طلبك. فعّل الدفع لتفعيل الإضافة.');
      await loadAddons();
      await refreshSubscription?.();
    } catch (err) {
      toast.error(err.response?.data?.message || 'فشل في إكمال الطلب.');
    } finally {
      setPurchasing(false);
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard?.writeText(text);
    toast.success('تم النسخ');
  };

  return (
    <div className="min-h-screen bg-[#F8FAFC]" dir="rtl">
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.35 }}
        className="max-w-[1600px] mx-auto px-4 sm:px-6 lg:px-8 py-6 lg:py-8 space-y-6"
      >
        {/* Header */}
        <div className="flex items-start justify-between gap-4">
          <div>
            <div className="flex items-center gap-3 mb-1">
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-blue-600 to-blue-700 flex items-center justify-center shadow-sm">
                <Globe className="w-5 h-5 text-white" />
              </div>
              <h1 className="text-2xl font-bold text-gray-800">الدومين والنشر</h1>
            </div>
            <p className="text-gray-500 text-sm mr-1">
              تحكّم في عنوان موقعك التعريفي وربطه بدومين خاص لو عندك
            </p>
          </div>
        </div>

        {/* Default subdomain (always available) */}
        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
          <div className="p-6">
            <div className="flex items-center gap-2 mb-4">
              <ShieldCheck className="w-4 h-4 text-emerald-600" />
              <span className="text-sm font-semibold text-emerald-700">الدومين الافتراضي — مفعّل ومجاني</span>
            </div>
            <p className="text-sm text-gray-600 mb-3">عنوان موقعك الرسمي على منصّة LabTechs:</p>
            <div className="flex items-center gap-2 p-4 rounded-xl bg-gray-50 border border-gray-100">
              <code className="flex-1 text-sm text-gray-800 font-mono truncate">
                {subdomainUrl || `https://<slug>.${LISTECHS_SITES_ROOT}`}
              </code>
              {subdomainUrl ? (
                <button
                  onClick={() => copyToClipboard(subdomainUrl)}
                  className="p-2 rounded-lg text-gray-500 hover:text-gray-700 hover:bg-white transition-colors"
                  title="نسخ"
                >
                  <Copy className="w-4 h-4" />
                </button>
              ) : null}
            </div>
            {!slug && (
              <p className="text-xs text-amber-600 mt-3">
                سيتم توليد الرابط بعد إكمال تفعيل الموقع من الداش بورد.
              </p>
            )}
          </div>
        </div>

        {/* Custom domain section */}
        {canUseCustomDomain ? (
          <CustomDomainsManager />
        ) : loadingAddons ? (
          /* Loading state */
          <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-10 flex items-center justify-center">
            <div className="w-8 h-8 border-4 border-blue-200 border-t-blue-600 rounded-full animate-spin" />
          </div>
        ) : (
          /* States 2 & 3: feature not enabled → offer upgrade + (optionally) add-on */
          <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
            <div className="relative bg-gradient-to-br from-amber-50 to-orange-50 px-6 py-5 border-b border-amber-100">
              <div className="flex items-center gap-2">
                <div className="w-9 h-9 rounded-lg bg-amber-100 flex items-center justify-center">
                  <Crown className="w-4.5 h-4.5 text-amber-600" />
                </div>
                <div>
                  <h3 className="text-sm font-bold text-amber-900">الدومين المخصص غير مفعّل في باقتك الحالية</h3>
                  <p className="text-xs text-amber-700 mt-0.5">
                    اختر إحدى الطريقتين لتفعيل الميزة على حسابك
                  </p>
                </div>
              </div>
            </div>

            <div className="grid md:grid-cols-2 gap-4 p-6">
              {/* Option 1: Upgrade plan */}
              <div className="rounded-2xl border border-gray-100 p-5 hover:border-blue-200 transition-colors">
                <div className="flex items-center gap-2 mb-3">
                  <div className="w-8 h-8 rounded-lg bg-blue-50 flex items-center justify-center">
                    <Sparkles className="w-4 h-4 text-blue-600" />
                  </div>
                  <h4 className="text-sm font-bold text-gray-800">ترقية الباقة</h4>
                </div>
                <p className="text-xs text-gray-600 mb-4 leading-relaxed">
                  ترقَّ إلى باقة أعلى تشمل الدومين المخصص + ميزات متقدمة أخرى للمختبرات الكبيرة.
                </p>
                <button
                  onClick={() => navigate('/settings/subscription/upgrade')}
                  className="w-full px-4 py-2.5 bg-gradient-to-r from-blue-600 to-blue-700 text-white rounded-xl text-sm font-medium hover:from-blue-700 hover:to-blue-800 transition-all shadow-sm"
                >
                  عرض الباقات الأعلى
                </button>
              </div>

              {/* Option 2: Buy as add-on */}
              <div className={`rounded-2xl border p-5 transition-colors ${canPurchaseAddon ? 'border-emerald-100 bg-emerald-50/30' : 'border-gray-100 bg-gray-50/50 opacity-70'}`}>
                <div className="flex items-center gap-2 mb-3">
                  <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${canPurchaseAddon ? 'bg-emerald-100' : 'bg-gray-100'}`}>
                    <Link2 className={`w-4 h-4 ${canPurchaseAddon ? 'text-emerald-600' : 'text-gray-400'}`} />
                  </div>
                  <h4 className="text-sm font-bold text-gray-800">شراء كإضافة</h4>
                </div>

                {canPurchaseAddon ? (
                  <>
                    <p className="text-xs text-gray-600 mb-4 leading-relaxed">
                      أضف الدومين المخصص فقط دون ترقية باقتك.
                    </p>

                    {/* Billing cycle toggle */}
                    <div className="flex rounded-lg border border-gray-200 p-0.5 mb-3 bg-white">
                      {['monthly', 'yearly'].map((c) => (
                        <button
                          key={c}
                          onClick={() => setBillingCycle(c)}
                          className={`flex-1 px-3 py-1.5 rounded-md text-xs font-medium transition-colors ${
                            billingCycle === c
                              ? 'bg-emerald-600 text-white'
                              : 'text-gray-600 hover:text-gray-800'
                          }`}
                        >
                          {c === 'monthly' ? 'شهري' : 'سنوي'}
                        </button>
                      ))}
                    </div>

                    <div className="flex items-baseline gap-1 mb-4">
                      <span className="text-2xl font-bold text-emerald-700">
                        {billingCycle === 'monthly'
                          ? customDomainAddon.pricing.monthly
                          : customDomainAddon.pricing.yearly}
                      </span>
                      <span className="text-sm text-gray-600">
                        {customDomainAddon.pricing.currency} / {billingCycle === 'monthly' ? 'شهر' : 'سنة'}
                      </span>
                    </div>

                    {ownedAddon?.status === 'pending_payment' ? (
                      <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-amber-50 text-amber-700 text-xs">
                        <Clock className="w-4 h-4" />
                        <span>طلبك قيد انتظار الدفع</span>
                      </div>
                    ) : (
                      <button
                        onClick={onPurchase}
                        disabled={purchasing}
                        className="w-full px-4 py-2.5 bg-gradient-to-r from-emerald-600 to-emerald-700 text-white rounded-xl text-sm font-medium hover:from-emerald-700 hover:to-emerald-800 transition-all shadow-sm disabled:opacity-60 disabled:cursor-not-allowed"
                      >
                        {purchasing ? 'جاري المعالجة...' : 'شراء الإضافة'}
                      </button>
                    )}
                  </>
                ) : (
                  <>
                    <p className="text-xs text-gray-500 mb-4 leading-relaxed">
                      هذه الميزة لا تتوفر كإضافة على باقتك الحالية. ترقية الباقة هي الخيار المتاح.
                    </p>
                    <button
                      disabled
                      className="w-full px-4 py-2.5 bg-gray-200 text-gray-500 rounded-xl text-sm font-medium cursor-not-allowed"
                    >
                      غير متاح
                    </button>
                  </>
                )}
              </div>
            </div>

            {/* Note for transparency */}
            <div className="px-6 pb-5">
              <p className="text-xs text-gray-400 flex items-start gap-2">
                <AlertCircle className="w-3.5 h-3.5 shrink-0 mt-0.5" />
                السعر يحدده مسؤول النظام وقابل للتعديل في أي وقت. بعد الدفع، ستتمكن من ربط دومينك خلال دقائق.
              </p>
            </div>
          </div>
        )}

        {/* Verified badge if owned add-on is active */}
        {ownedAddon?.status === 'active' && (
          <div className="flex items-center gap-3 p-4 rounded-xl bg-emerald-50 border border-emerald-100">
            <CheckCircle2 className="w-5 h-5 text-emerald-600 shrink-0" />
            <p className="text-sm text-emerald-800">
              إضافة الدومين المخصص نشطة حتى{' '}
              {new Date(ownedAddon.expiresAt).toLocaleDateString('ar-SA')}
            </p>
          </div>
        )}
      </motion.div>
    </div>
  );
};

export default DomainManagement;
